import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { SECTION_NAMES } from '@repo/common';
import Image from 'shared/components/Image';
import StringMapper from 'shared/components/StringMapper';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import ReactVirtualGrid from 'shared/components/VirtualizedGrid';
import { HORIZONTAL_GRID_GAME_CARD_SIZE } from 'shared/components/VirtualizedGrid/hooks/useHorizontalGridDimensions';
import { buildPortalAssetUrl } from 'shared/utils/helpers';
import { useAppStore } from 'store/app/appStore';
import { GET_PRIZE_TEXT_GIFT_SPIN, GET_PRIZE_TEXT_MONEY } from '../../../../../../../../constants';
import { useSelectedCampaignId } from '../../../../../../store/campaignsStore';
import { TOURNAMENTS_CAMPAIGN_TYPE, type ITournamentPrize } from '../../../../../../types/variants/tournament.types';
import { getPrizeText } from '../../../../../../utils/campaignHelpers';
import { resolveCampaignGames } from '../../../../../../utils/resolveCampaignGames';

import styles from './FinishedTournamentReward.module.css';

const FinishedPrizeText = styled.div<{ $isMoneyPrize: boolean; $fontScaleFactor: number | undefined }>`
  display: flex;
  align-items: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'center' : 'baseline')};
  position: absolute;
  font-family: var(--font-family-secondary, inherit);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 60px;
  ${({ $fontScaleFactor }) =>
    $fontScaleFactor !== undefined && `font-size: clamp(40px, calc(60px - ${$fontScaleFactor}px), 60px);`}
  letter-spacing: 4px;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;

  background-image: repeating-linear-gradient(
    to bottom,
    #fff439 0%,
    #fffda4 28%,
    #f9c419 40%,
    #c59029 65%,
    #b16c04 77%,
    #fbce35 92%
  );
  background-size: 150%;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: 0.34px #fff439;

  span {
    writing-mode: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'vertical-lr' : 'unset')};
    text-orientation: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'upright' : 'unset')};
    font-weight: ${({ $isMoneyPrize }) => ($isMoneyPrize ? '800' : 'unset')};
    font-size: ${({ $isMoneyPrize }) => ($isMoneyPrize ? '28px' : '19px')};
    -webkit-text-stroke: 0;
    -webkit-text-fill-color: ${({ $isMoneyPrize }) => ($isMoneyPrize ? '#fff439' : 'unset')};
    font-family: ${({ $isMoneyPrize }) =>
      $isMoneyPrize ? 'var(--font-family-secondary, inherit)' : 'var(--font-family-tertiary, inherit)'};
    line-height: 1;
    letter-spacing: 0;
    text-align: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'center' : 'left')};
    position: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'unset' : 'relative')};
    top: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'unset' : '-20px')};
    width: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'unset' : 'min-content')};
    text-transform: uppercase;
    background-size: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'unset' : '150%')};
    background-image: ${({ $isMoneyPrize }) =>
      $isMoneyPrize
        ? 'unset'
        : 'repeating-linear-gradient(to bottom, #FFF439 0%, #FFFDA4 8%, #F9C419 69%, #E39600 80%, #FBCE35 92%);'};
    background-clip: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'unset' : 'text')};
    -webkit-text-fill-color: ${({ $isMoneyPrize }) => ($isMoneyPrize ? 'unset' : 'transparent')};
    text-wrap: auto;
  }

  .rewards_currency {
    width: 10px;
    height: 15px;
    font-size: 50%;
    -webkit-text-stroke: 0;
    -webkit-text-fill-color: #fff439;
  }
`;

const SmallText = styled(StringMapper)`
  display: flex;
`;

const MessageText = styled.div<{ $noWin?: boolean; $winText?: string }>`
  position: relative;
  color: #fff;
  font-weight: 500;

  &::before {
    content: ${({ $noWin, $winText }) => (!$noWin && $winText ? JSON.stringify($winText) : 'none')};
    position: absolute;
    inset: 0;
    z-index: -1;
    color: transparent;
    -webkit-text-fill-color: transparent;
    -webkit-text-stroke: 2px #FBCE35;
  }
`;

const GiftSpinGamesList = ({ gameKeys }: { gameKeys: string[] }) => {
  const tournamentId = useSelectedCampaignId(TOURNAMENTS_CAMPAIGN_TYPE);

  const supportedGames = useAppStore((state) => state.supportedGames);
  const topGames = useAppStore((state) => state.sections.find((section) => section.name === SECTION_NAMES.TOP)?.games);

  const giftSpinsGames = useMemo(() => {
    const topGameKeys = new Set(topGames?.map((game) => game.gameKey) ?? []);

    return resolveCampaignGames(gameKeys, supportedGames, topGameKeys);
  }, [gameKeys, supportedGames, topGames]);

  return (
    <div className={styles.giftSpinGamesList}>
      <ReactVirtualGrid
        games={giftSpinsGames}
        isFav={false}
        sectionName={`TOURNAMENT_${tournamentId}_GIFT_SPINS_REWARD`}
        hasJackpotPanel={false}
        gameCardSize={HORIZONTAL_GRID_GAME_CARD_SIZE.COMPACT}
      />
    </div>
  );
};

const FinishedTournamentReward = ({ prize }: { prize?: ITournamentPrize | null }) => {
  const { t } = useTranslation();

  const isMoneyPrize = prize?.type === GET_PRIZE_TEXT_MONEY;
  const isGiftSpinPrize = prize?.type === GET_PRIZE_TEXT_GIFT_SPIN;

  const giftSpinGameKeys = prize?.giftSpinConfig?.games ?? [];
  const hasGiftSpinGames = isGiftSpinPrize && giftSpinGameKeys.length > 0;

  const winTitle = t('tournaments.finished_tournament_rewards.you_have_won', 'YOU HAVE WON!');
  const formattedAmount = getPrizeText({ type: prize?.type || '', name: prize?.name || '' });
  const fontScaleFactor = formattedAmount ? (formattedAmount.length > 7 ? formattedAmount.length : 0) : undefined;

  const confettiBg = `url(${buildPortalAssetUrl('/campaigns/icons/confetty.webp')})`;
  const cupPath = prize?.place && prize.place <= 3
    ? `/campaigns/icons/rewardsPosition${prize.place}`
    : '/campaigns/icons/cup';

  return (
    <div
      className={styles.rewardContainer}
      data-is-finished={true}
      style={{ '--confetti-url': confettiBg } as React.CSSProperties}
    >
      <div className={`${styles.rewardContent} ${hasGiftSpinGames ? styles.rewardContentWithGiftSpinGames : ''}`}>
        <Image
          className={styles.finishedPrizeImage}
          src={cupPath}
          alt={`Prize for place ${prize?.place ?? 'default'}`}
        />
        {prize ? (
          <>
            <div className={styles.textContentWrapper}>
              <MessageText className={styles.rewardText} $winText={winTitle}>
                {winTitle}
              </MessageText>
              <div className={styles.prizeTextContainer}>
                <FinishedPrizeText $isMoneyPrize={isMoneyPrize} $fontScaleFactor={fontScaleFactor}>
                  {formattedAmount}
                  {isMoneyPrize && prize.currencyCode && (
                    <span>
                        <SmallText
                            text={prize.currencyCode.toUpperCase() || ''}
                            letterClassName='rewards_currency'
                        />
                    </span>
                  )}
                  {isGiftSpinPrize && (
                    <span>
                      <TransWithFallback
                        i18nKey={`tournaments.gift_spin${prize.name !== '1' ? 's' : ''}`}
                        components={{
                          br: <br />,
                        }}
                      />
                    </span>
                  )}
                </FinishedPrizeText>
              </div>
              <div className={styles.secondaryText}>
                {isGiftSpinPrize ? (
                  prize.giftSpinConfig?.bet != null ? (
                    t('tournaments.finished_tournament_rewards.won_gift_spins_with_bet_games', {
                      amount: prize.giftSpinConfig.bet.toFixed(2),
                      currency: prize.currencyCode.toUpperCase(),
                      defaultValue: 'Enjoy your GIFT SPINS with a {{amount}} {{currency}} bet in these games:',
                    })
                  ) : (
                    t(
                      'tournaments.finished_tournament_rewards.won_gift_spins_games',
                      'Enjoy your GIFT SPINS in these games:',
                    )
                  )
                ) : (
                  <>
                    {prize.place < 4
                      ? t(
                          'tournaments.finished_tournament_rewards.won_top_three_tournaments',
                          'Congratulations! Feels good to be a winner? ',
                        )
                      : t(
                          'tournaments.finished_tournament_rewards.won_regular_prize_tournaments',
                          'Well done! Looking for bigger rewards?',
                        )}
                    {t(
                      'tournaments.finished_tournament_rewards.join_other_tournaments',
                      'Join the other tournaments!'
                    )}
                  </>
                )}
              </div>
            </div>
            {hasGiftSpinGames && <GiftSpinGamesList gameKeys={giftSpinGameKeys} />}
          </>
        ) : (
          <>
            <MessageText className={`${styles.rewardText} ${styles.noWin}`} $noWin={true}>
              {t(
                'tournaments.finished_tournament_rewards.no_win_tournaments',
                'The tournament has ended, but not the race! Looking for more?',
              )}
            </MessageText>
            <MessageText className={`${styles.rewardText} ${styles.noWin}`} $noWin={true}>
              {t(
                'tournaments.finished_tournament_rewards.join_other_tournaments',
                'Join the other tournaments!'
              )}
            </MessageText>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(FinishedTournamentReward);
